import { Text, UnstyledButton } from '@mantine/core';
import classes from './cohort-strip.module.css';

export interface CohortStripItem {
  id: string;
  label: string;
  /** One value per rung, top first. null renders as a gap, not as zero. */
  values: (number | null)[];
}

interface CohortStripProps {
  items: CohortStripItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}

/**
 * Every cohort as a miniature funnel, side by side.
 *
 * Small multiples answer the question a single cohort cannot: whether this
 * week's shape is normal. They also make the young cohorts legible — a funnel
 * that visibly stops partway through says "not measured yet" far better than
 * an em dash in a table cell does.
 */
export function CohortStrip({ items, selectedId, onSelect }: CohortStripProps) {
  return (
    <div className={classes.scroller}>
      <div className={classes.strip}>
        {items.map((item) => {
          const base = item.values[0] ?? 0;

          return (
            <UnstyledButton
              key={item.id}
              onClick={() => onSelect(item.id)}
              className={classes.cell}
              data-selected={item.id === selectedId || undefined}
              aria-pressed={item.id === selectedId}
            >
              <div className={classes.mini}>
                {item.values.map((value, index) => (
                  <div
                    key={index}
                    className={value == null ? classes.miniGap : classes.miniBar}
                    style={
                      value == null || base === 0
                        ? undefined
                        : { width: `${Math.max(6, (value / base) * 100)}%` }
                    }
                  />
                ))}
              </div>
              <Text size="xs" c="dimmed" ta="center" lh={1.2}>
                {item.label}
              </Text>
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
}
